import { Alert, Button, message, Modal, Result, Space, Spin } from 'antd';
import React, { useImperativeHandle, useRef, useState } from 'react';
import intl from 'react-intl-universal';
import { LoadingOutlined } from '@ant-design/icons';
import usePollAnimationFrame from '@/hooks/usePollAnimationFrame';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import { checkRoomCard } from '@/api/card';
import { LockVendorMap } from '../../config';
import {
  CardOperateProcessingStatus,
  emptyAndDataAccuracyStrategyMap,
  ICardInfo,
  MyDoorLockCardClass,
  SingleCardProxyClass,
} from '../helper';
import styles from '../index.module.scss';

const DeleteDoorCardModal = ({ innerRef, enabledVendor, roomIds, roomNo, checkCallBack }) => {
  const [open, setOpen] = useState(false);

  const [requestStatus, setRequestStatus] = useState<CardOperateProcessingStatus>(
    CardOperateProcessingStatus.Init,
  );

  const instanceRef = useRef<MyDoorLockCardClass>();
  const usePoll = usePollAnimationFrame();
  // 连接实例
  const cardInstance: MyDoorLockCardClass = new SingleCardProxyClass();
  instanceRef.current = cardInstance;

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  const deleteCardInfo = async ({ localCardInfo }) => {
    try {
      if (instanceRef.current) {
        await instanceRef.current.deleteCard({ lockType: LockVendorMap[enabledVendor?.lockCode] });
      }

      const { card_id: cardNo } = localCardInfo;
      const { status } = await checkRoomCard({
        roomIds,
        cardNo,
      });
      if (status) {
        message.success(intl.get('cpm_cards_return_success'));
        if (checkCallBack && typeof checkCallBack === 'function') checkCallBack();
      }
    } catch (error) {
      throw new Error(error.msg || '退卡链路失败');
    }
  };

  const pollDeleteCard = () => {
    const strategyObj = emptyAndDataAccuracyStrategyMap[enabledVendor?.lockCode];

    setRequestStatus(CardOperateProcessingStatus.Loading);
    usePoll
      .startPoll(
        () => cardInstance.readCard({ lockType: LockVendorMap[enabledVendor?.lockCode] }),
        strategyObj.successFulCodeList,
      )
      .then(
        async (res) => {
          console.log('readData', res);
          const isEmpty = strategyObj.isEmptyCard(res);
          const localCardInfo: ICardInfo = strategyObj.updateCardStatus(res);

          if (!isEmpty) {
            try {
              await deleteCardInfo({ localCardInfo });
              setRequestStatus(CardOperateProcessingStatus.RequestSuccess);

              endUploadDoorInfo({ result: UploadStatus.Success });
            } catch (error) {
              setRequestStatus(CardOperateProcessingStatus.RequestError);

              endUploadDoorInfo({ result: UploadStatus.Fail, remark: error.msg });
            }
          } else {
            // 空白卡直接异常
            setRequestStatus(CardOperateProcessingStatus.RequestError);
          }
        },
        (err) => {
          console.log('receive=>>>>error', err.message);
          setRequestStatus(CardOperateProcessingStatus.RequestError);
        },
      )
      .catch(() => {
        setRequestStatus(CardOperateProcessingStatus.RequestError);
      });
  };

  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Check, roomNo });

    setRequestStatus(CardOperateProcessingStatus.Init);

    pollDeleteCard();
  };

  const closeModal = () => {
    setOpen(false);

    usePoll.endPoll();
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal: () => {
        setOpen(true);

        retry();
      },
      closeModal,
    }),
    [],
  );

  const isException = requestStatus === CardOperateProcessingStatus.RequestError;

  return (
    <>
      <Modal
        width={560}
        title={intl.get('cpm_cards_return')}
        open={open}
        maskClosable={false}
        keyboard={false}
        footer={
          <>
            <Space align="start" size={16}>
              {isException && (
                <Button type="primary" onClick={() => retry()}>
                  {intl.get('app_confirm_retry_btn')}
                </Button>
              )}
              <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
            </Space>
          </>
        }
        destroyOnClose
        onCancel={() => closeModal()}
      >
        {isException ? (
          <Alert message={intl.get('cpm_cards_abnormal_canot_update')} type="error" showIcon />
        ) : (
          <Alert message={intl.get('cpm_cards_put_on_write_mechine')} type="info" showIcon />
        )}

        {isException ? (
          <Result
            style={{ height: 200 }}
            status="error"
            subTitle={intl.get('cpm_cards_info_abnormal')}
          />
        ) : (
          <div className={styles.readCardLoading}>
            <Spin
              tip={intl.get('cpm_cards_info_reding')}
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default DeleteDoorCardModal;
